// src/app/services/seo.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ImageService } from './image.service';
import { siteConfig } from '../config/site-config';

interface OpenGraphTags {
  title: string;
  description: string;
  image?: string; // kann ID, relativer Pfad oder absolute URL sein
  url?: string;   // Pfad oder absolute URL
  type?: string;  // 'article' | 'website' | ...
}

interface BlogSchema {
  headline: string;
  description: string;
  category: string;
  timeRequired: number; // Minuten
  slug: string;
  image?: string; // ID/rel./abs.
  imageWidth?: number;
  imageHeight?: number;
  imageCaption?: string;
  datePublished: Date;
  dateModified?: Date;
  author?: string;
  authorType?: 'Person' | 'Organization';
  keywords: string[];
  aboutName?: string;   // z. B. "Echter Lorbeer (Laurus nobilis)"
  wordCount?: number;
}

interface ArticleSchema {
  headline: string;
  description: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  datePublished: Date;
  dateModified?: Date;
  author?: string;
  authorType?: 'Person' | 'Organization';
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly websiteName = siteConfig.brand.websiteName;
  private readonly liveUrl = environment.liveUrl; // absolute Basis-URL, ohne trailing Slash
  private readonly defaultImage = siteConfig.seo.defaultImageUrl;
  private isBrowser: boolean;

  private lastJsonLd: unknown | null = null;
  
  constructor(
    private title: Title,
    private meta: Meta,
    private router: Router,
    private imageService: ImageService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  // Hilfen

  private baseUrlNoTrailing(): string {
    return this.liveUrl.replace(/\/+$/, '');
  }

  private absoluteUrl(pathOrUrl: string): string {
    try {
      return new URL(pathOrUrl, this.baseUrlNoTrailing() + '/').toString();
    } catch {
      return this.baseUrlNoTrailing();
    }
  }

  private currentPathNoQueryHash(): string {
    const url = this.router.url || '/';
    return url.split('#')[0].split('?')[0] || '/';
  }

  private upsertJsonLd(key: string, data: unknown): void {
    if(!this.isBrowser) return;
    
    const selector = `script[type="application/ld+json"][data-ld="${key}"]`;
    this.document.querySelectorAll(selector).forEach(s => s.remove());
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-ld', key);
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  private getAbsoluteSocialImageUrl(input?: string): string {
    const source = input && input.trim().length > 0 ? input : this.defaultImage;
    const transformed = this.imageService.transformImageLink(source);
    return this.absoluteUrl(transformed);
  }

  private buildImageObject(url: string, width?: number, height?: number, caption?: string) {
    const obj: any = {
      '@type': 'ImageObject',
      '@id': url,
      url,
      representativeOfPage: true
    };
    if (width && width > 0) obj.width = width;
    if (height && height > 0) obj.height = height;
    if (caption && caption.trim().length > 0) obj.caption = caption;
    return obj;
  }

  setTitle(pageTitle: string): void {
    this.title.setTitle(`${pageTitle} | ${this.websiteName}`);
  }

  setDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
  }

  setCanonicalUrl(url?: string): void {
    const path = (url ?? this.currentPathNoQueryHash());
    const canonicalUrl = this.absoluteUrl(path);

    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  setNoIndex(noIndex: boolean = true): void {
    this.meta.updateTag({
      name: 'robots',
      content: noIndex ? 'noindex, nofollow' : 'index, follow'
    });
  }

  set404Page(): void {
    // Hinweis: HTTP-Status 404 zusätzlich serverseitig setzen (Express-Server von Angular Universal).
    this.setTitle('Seite nicht gefunden');
    this.setDescription('Die gesuchte Seite konnte nicht gefunden werden.');
    this.setNoIndex();
  }

  setOpenGraphTags(tags: OpenGraphTags): void {
    const path = (tags.url ?? this.currentPathNoQueryHash()).split('#')[0];
    const pageUrl = this.absoluteUrl(path);
    const imageUrl = this.getAbsoluteSocialImageUrl(tags.image);

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: tags.title });
    this.meta.updateTag({ property: 'og:description', content: tags.description });
    this.meta.updateTag({ property: 'og:site_name', content: this.websiteName });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:type', content: tags.type || 'website' });

    // Bild
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:image:secure_url', content: imageUrl });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: tags.title });
    this.meta.updateTag({ name: 'twitter:description', content: tags.description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
  }

  setBlogPostingSchema(blog: BlogSchema, html: string): void {
    const base = this.baseUrlNoTrailing();
    const url = `${base}/blog/${blog.slug}`;
    const imageUrl = this.getAbsoluteSocialImageUrl(blog.image);

    const authorType = siteConfig.seo.authorType;
    const authorName = siteConfig.brand.teamName;

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': url,
      url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      isPartOf: {
        '@type': 'Blog',
        name: siteConfig.seo.blogName,
        url: `${base}/blog`
      },
      inLanguage: siteConfig.locale.language,
      isAccessibleForFree: true,

      headline: blog.headline,
      name: blog.headline,
      description: blog.description,
      articleSection: blog.category,

      keywords: blog.keywords && blog.keywords.length ? blog.keywords : undefined,
      about: blog.aboutName ? { '@type': 'Thing', name: blog.aboutName } : undefined,
      wordCount: this.countWords(html),

      datePublished: blog.datePublished.toISOString(),
      dateModified: (blog.dateModified ?? blog.datePublished).toISOString(),
      timeRequired: 'PT' + blog.timeRequired + 'M',

      image: this.buildImageObject(imageUrl, blog.imageWidth, blog.imageHeight, blog.imageCaption),
      thumbnailUrl: imageUrl,

      author: { '@type': authorType, name: authorName },

      publisher: {
        '@type': 'Organization',
        name: this.websiteName,
        url: base + '/',
        logo: {
          '@type': 'ImageObject',
          url: siteConfig.seo.organizationLogoUrl
          // Optional: width/height ergänzen, wenn bekannt
        }
      },

      potentialAction: {
        '@type': 'ReadAction',
        target: url
      }
    };

    // Entferne undefinierte Properties sauber
    const cleaned = JSON.parse(JSON.stringify(schema));

    this.lastJsonLd = cleaned;
    this.upsertJsonLd('article', cleaned);
  }

  setArticleSchema(article: ArticleSchema): void {
    const url = this.absoluteUrl(this.currentPathNoQueryHash());
    const imageUrl = this.getAbsoluteSocialImageUrl(article.image);
    const authorType = article.authorType ?? 'Person';

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': url,
      url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      inLanguage: siteConfig.locale.language,

      headline: article.headline,
      name: article.headline,
      description: article.description,

      image: this.buildImageObject(imageUrl, article.imageWidth, article.imageHeight),

      datePublished: article.datePublished.toISOString(),
      dateModified: (article.dateModified ?? article.datePublished).toISOString(),
      author: article.author ? { '@type': authorType, name: article.author } : undefined,
      publisher: {
        '@type': 'Organization',
        name: this.websiteName,
        logo: { '@type': 'ImageObject', url: siteConfig.seo.organizationLogoUrl }
      }
    };

    const cleaned = JSON.parse(JSON.stringify(schema));

    this.lastJsonLd = cleaned;
    this.upsertJsonLd('article', cleaned);
  }

  getJsonLd(): unknown | null {
    return this.lastJsonLd;
  }

  getDefaultImageUrl(){
    return this.defaultImage
  }

  setStructuredData(data: unknown, key: string = 'generic'): void {
    if (!data) return;
    this.lastJsonLd = data;
    this.upsertJsonLd(key, data);
  }

  setBreadcrumbSchema(breadcrumbs: { name: string, url: string }[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: this.absoluteUrl(crumb.url)
      }))
    };
    
    this.upsertJsonLd('breadcrumb', schema);
  }
  
  setFaqSchema(faqs: { q: string, a: string }[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    };
    
    this.upsertJsonLd('faq', schema);
  }

// Zählt Wörter in einem HTML-String solide, auch serverseitig (SSR).
countWords(html: string): number {
  if (!html || typeof html !== 'string') return 0;

  // 1) Offensichtliches rauswerfen (Kommentare, Skripte, Styles, SVG/MathML, Noscript)
  let cleaned = html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(script|style|svg|math|noscript)[\s\S]*?<\/\1>/gi, ' ');

  // 2) Zeilen-/Blocktrenner auf Leerzeichen mappen, damit Wörter nicht verkleben
  cleaned = cleaned.replace(/<(br|p|div|section|article|li|ul|ol|h[1-6]|blockquote|pre|table|tr|td|th)\b[^>]*>/gi, ' ');

  // 3) Alle übrigen Tags entfernen
  cleaned = cleaned.replace(/<[^>]+>/g, ' ');

  // 4) HTML-Entities decodieren (&nbsp; etc.)
  cleaned = this.decodeHtml(cleaned);

  // 5) Whitespaces normalisieren
  cleaned = cleaned
    .replace(/\u00A0/g, ' ')   // nbsp -> Space
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return 0;

  // 6) Wörter zählen:
  // \p{L} = Buchstaben (inkl. Umlaute), \p{N} = Ziffern
  // Erlaubt Bindestrich-Wörter (z. B. "Herbst-Schnitt")
  const tokens = cleaned.match(/[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*/gu);
  return tokens ? tokens.length : 0;
}

// HTML-Entity-Decoding: nutzt im Browser den DOM, fällt bei SSR auf eine simple Variante zurück.
private decodeHtml(input: string): string {
  try {
    if (this.isBrowser && this.document) {
      const ta = this.document.createElement('textarea');
      ta.innerHTML = input;
      return ta.value;
    }
  } catch {
    // ignore and fall back
  }
  // Minimaler Fallback (SSR), deckt die häufigsten Entities ab:
  return input
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
}



